import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import { removeLoadingKey } from "../helpers/common";
import {
  DEFAULT_COLUMNS,
  normalizeCatalogColumns,
  normalizeCatalogColumnsByView,
  normalizeProductCatalogCustomViews,
} from "../consts/productCatalog";

const METADATA_PRODUCT_CATALOG_COLUMNS = "/api/v1/metadata/product-catalog-columns";
const METADATA_PRODUCT_CATALOG_DEFAULTS_SETTINGS = "/api/v1/metadata/product-catalog-defaults-settings";
const METADATA_TIER_DISCOUNT_DRIVERS = "/api/v1/metadata/tier-discount-drivers";
const PRODUCT_CATALOG_TABLE_URL = "/api/v1/product-catalog";
const PRODUCT_CATALOG_CATEGORY_SETTINGS_URL = "/api/v1/product-catalog/settings/categories";
const PRODUCT_CATALOG_PRESET_SETTINGS_URL = "/api/v1/product-catalog/settings/presets";
const PRODUCT_CATALOG_CONTAINER_SETTINGS_URL = "/api/v1/product-catalog/settings/container-groups";
const PRODUCT_CATALOG_CHANNEL_PRODUCTS_URL = "/api/v1/product-catalog/channel-products";
const PRODUCT_CATALOG_CATALOG_SKUS_URL = "/api/v1/product-catalog/catalog-skus";
const PRODUCT_CATALOG_PRODUCT_SETTINGS_URL = "/api/v1/product-catalog/product-settings";
const PRODUCT_CATALOG_CREATE_PRODUCT_URL = "/api/v1/product-catalog/create-product";
const PRODUCT_CATALOG_RESTORE_PRODUCT_URL = "/api/v1/product-catalog/restore-product";
const PRODUCT_CATALOG_SYNC_METAFIELDS_TO_SHOPIFY_URL = "/api/v1/product-catalog/sync-metafields-to-shopify";
const PRODUCT_CATALOG_COLUMNS_FIELD = "product_catalog_columns";
const PRODUCT_CATALOG_DEFAULTS_SETTINGS_FIELD = "product_catalog_defaults_settings";
const TIER_DISCOUNT_DRIVERS_FIELD = "tier_discount_drivers";

const toLeadTimeNumber = (value) => {
  if (value === null || value === undefined || value === "" || value === "-") return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

function normalizeProductCatalogColumnsPayload(data) {
  const root = data?.[PRODUCT_CATALOG_COLUMNS_FIELD] ?? data ?? {};
  const inner = typeof root === "object" && root !== null ? root : {};
  const byView =
    inner.by_view ?? inner.byView ?? inner.groups ?? inner.views ?? {};
  const rawByView =
    typeof byView === "object" && byView !== null && !Array.isArray(byView)
      ? byView
      : {};
  return {
    by_view: normalizeCatalogColumnsByView(rawByView),
    custom_views: normalizeProductCatalogCustomViews(inner.custom_views ?? inner.customViews),
  };
}

function buildProductCatalogColumnsBody(productCatalogColumns) {
  return {
    [PRODUCT_CATALOG_COLUMNS_FIELD]: {
      by_view: productCatalogColumns.by_view ?? {},
      ...(Array.isArray(productCatalogColumns.custom_views)
        ? { custom_views: normalizeProductCatalogCustomViews(productCatalogColumns.custom_views) }
        : {}),
    },
  };
}

function normalizeProductCatalogDefaultsSettingsPayload(data) {
  const root = data?.[PRODUCT_CATALOG_DEFAULTS_SETTINGS_FIELD] ?? data ?? {};
  const inner = typeof root === "object" && root !== null ? root : {};
  const defaultColumns = normalizeCatalogColumns(
    Array.isArray(inner.default_columns) ? inner.default_columns : DEFAULT_COLUMNS
  );
  const defaultPaginationPages = Number(inner.default_pagination_pages) || 50;

  return {
    default_columns: defaultColumns,
    default_pagination_pages: defaultPaginationPages,
  };
}

function buildProductCatalogDefaultsSettingsBody(settings) {
  return {
    [PRODUCT_CATALOG_DEFAULTS_SETTINGS_FIELD]: {
      default_columns: settings.default_columns ?? DEFAULT_COLUMNS,
      default_pagination_pages: Number(settings.default_pagination_pages) || 50,
    },
  };
}

function normalizeTierDiscountDriversPayload(data) {
  const root = data?.[TIER_DISCOUNT_DRIVERS_FIELD] ?? data ?? {};
  const values = root?.by_demand_tier_id ?? root?.byDemandTierId ?? {};
  return {
    by_demand_tier_id:
      values && typeof values === "object" && !Array.isArray(values)
        ? Object.fromEntries(
            Object.entries(values)
              .filter(([id]) => /^\d+$/.test(String(id)))
              .map(([id, value]) => [String(id), value == null ? null : Number(value)])
          )
        : {},
  };
}

/**
 * Patches one catalog table row after `updateProductCatalogProductSettings` succeeds.
 * Only fields that were actually sent in the thunk `arg` are merged from `updated`.
 * Otherwise list-only enrichments (e.g. color from SKU mapping) are preserved — the API
 * always returns full `item` with placeholders ("-") that would wipe those cells if we
 * merged the entire response over the row.
 */
function mergeCatalogTableRowAfterSettingsUpdate(row, updated, changedKeys) {
  if (!updated?.id || row.id !== updated.id) return row;
  const keys = new Set(changedKeys.filter((k) => k && k !== "productId"));
  if (keys.size === 0) return row;

  const next = { ...row };

  if (keys.has("brand_pillar_id")) {
    next.brand_pillar = updated.brand_pillar || "-";
    next.brand_pillar_id = updated.brand_pillar_id ?? null;
  }
  if (keys.has("demand_tier_id")) {
    next.demand_tier = updated.demand_tier || "-";
    next.demand_tier_id = updated.demand_tier_id ?? null;
  }
  if (keys.has("intent_id")) {
    next.intent = updated.intent || "-";
    next.intent_id = updated.intent_id ?? null;
  }
  if (keys.has("entry_engine_id")) {
    next.entry_engine = updated.entry_engine || "-";
    next.entry_engine_id = updated.entry_engine_id ?? null;
  }
  if (keys.has("status")) {
    next.status = updated.status || "-";
  }
  if (keys.has("category_group_id") || keys.has("subcategory_variant_id")) {
    next.category_group_id = updated.category_group_id ?? null;
    next.subcategory_variant_id = updated.subcategory_variant_id ?? null;
    next.category = updated.category ?? next.category;
    next.subcategory = updated.subcategory ?? next.subcategory;
  }
  if (keys.has("unit_weight_lbs")) {
    next.unit_weight_lbs =
      updated.weight !== undefined && updated.weight !== null ? updated.weight : next.unit_weight_lbs;
  }
  if (keys.has("unit_length") || keys.has("unit_width") || keys.has("unit_height")) {
    next.unit_length =
      updated.length !== undefined && updated.length !== null ? updated.length : next.unit_length;
    next.unit_width =
      updated.width !== undefined && updated.width !== null ? updated.width : next.unit_width;
    next.unit_height =
      updated.height !== undefined && updated.height !== null ? updated.height : next.unit_height;
    next.cubic_inches =
      updated.length != null &&
      updated.length !== undefined &&
      updated.width != null &&
      updated.width !== undefined &&
      updated.height != null &&
      updated.height !== undefined
        ? Number(
            (Number(updated.length) * Number(updated.width) * Number(updated.height)).toFixed(2)
          )
        : "-";
  }
  if (keys.has("is_virtual")) {
    next.is_virtual = updated.is_virtual ?? false;
  }
  if (keys.has("is_packaging")) {
    next.is_packaging = updated.is_packaging ?? false;
  }
  if (keys.has("fbm_channel_product_id")) {
    next.fbm_channel_product_id = updated.fbm_channel_product_id ?? null;
    next.amazon_fbm_sku = updated.amazon_fbm_sku || "-";
  }
  if (keys.has("fba_channel_product_id")) {
    next.fba_channel_product_id = updated.fba_channel_product_id ?? null;
    next.amazon_fba_sku = updated.amazon_fba_sku || "-";
  }
  if (keys.has("packaging_product_id")) {
    next.packaging_product_id = updated.packaging_product_id ?? null;
    next.packaging_sku = updated.packaging_sku || "-";
  }
  if (keys.has("forecastable")) {
    next.forecastable = updated.forecastable ?? false;
  }
  if (keys.has("needs_review")) {
    next.needs_review = updated.needs_review ?? false;
  }
  if (keys.has("lock_flag")) {
    next.lock_flag = updated.lock_flag ?? false;
  }
  if (keys.has("replacement_exit") || keys.has("replacement_product_id")) {
    next.replacement_exit = updated.replacement_exit ?? false;
    next.replacement_product_id = updated.replacement_product_id ?? null;
    next.replaces_sku = updated.replaces_sku || "-";
  }
  if (keys.has("condition_of_product_id")) {
    next.condition_of_product_id = updated.condition_of_product_id ?? null;
    next.condition_label = updated.condition_label ?? null;
    next.condition_of_sku = updated.condition_of_sku || "-";
  }
  if (keys.has("lead_time_days")) {
    next.lead_time_days = updated.lead_time_days ?? "-";
  }
  if (keys.has("transit_lead_time_days")) {
    next.transit_lead_time_days = updated.transit_lead_time_days ?? "-";
  }
  if (keys.has("lead_time_days") || keys.has("transit_lead_time_days")) {
    const mfg = toLeadTimeNumber(updated.lead_time_days ?? next.lead_time_days);
    const transit = toLeadTimeNumber(updated.transit_lead_time_days ?? next.transit_lead_time_days);
    next.total_lead_time_days = mfg + transit;
  }
  if (keys.has("moq")) {
    next.moq = updated.moq ?? "-";
  }
  if (keys.has("container_group_id")) {
    next.container_group_id = updated.container_group_id ?? null;
    next.container_group = updated.container_group ?? next.container_group;
  }
  if (keys.has("container_qty")) {
    next.container_qty = updated.container_qty ?? "-";
  }
  if (keys.has("carton_qty")) {
    next.carton_qty = updated.carton_qty ?? "-";
  }
  if (keys.has("launch_date")) {
    next.launch_date = updated.launch_date ?? "-";
  }
  if (keys.has("sunset_date")) {
    next.sunset_date = updated.sunset_date ?? "-";
  }
  if (keys.has("inactive_date")) {
    next.inactive_date = updated.inactive_date ?? "-";
  }
  if (keys.has("color")) {
    next.color =
      updated.color !== undefined && updated.color !== null ? updated.color : next.color;
  }
  if (keys.has("material")) {
    next.material = updated.material ?? next.material;
    if (updated.material_primary_row_id !== undefined) {
      next.material_primary_row_id = updated.material_primary_row_id;
    }
  }
  if (keys.has("manual_msrp")) {
    next.manual_msrp = updated.manual_msrp ?? null;
    next.msrp = updated.msrp ?? next.msrp;
    next.msrp_from_shopify = updated.msrp_from_shopify ?? next.msrp_from_shopify;
    next.msrp_editable = updated.msrp_editable ?? next.msrp_editable;
    if (updated.sell_on_shopify !== undefined) {
      next.sell_on_shopify = updated.sell_on_shopify;
    }
  }
  for (const field of ["shopify_planned_gross_price", "amazon_planned_gross_price"]) {
    if (keys.has(field)) {
      next[field] = updated[field] ?? null;
      next[`${field}_meta`] = updated[`${field}_meta`] ?? { status: "missing" };
    }
  }

  return next;
}

export const ProductCatalogLoadingKeys = {
  fetchColumnsMetadata: "FETCH_PRODUCT_CATALOG_COLUMNS_METADATA",
  saveColumnsMetadata: "SAVE_PRODUCT_CATALOG_COLUMNS_METADATA",
  fetchDefaultsSettings: "FETCH_PRODUCT_CATALOG_DEFAULTS_SETTINGS",
  saveDefaultsSettings: "SAVE_PRODUCT_CATALOG_DEFAULTS_SETTINGS",
  fetchTierDiscountDrivers: "FETCH_TIER_DISCOUNT_DRIVERS",
  saveTierDiscountDrivers: "SAVE_TIER_DISCOUNT_DRIVERS",
  fetchCategorySettings: "FETCH_PRODUCT_CATALOG_CATEGORY_SETTINGS",
  fetchPresetSettings: "FETCH_PRODUCT_CATALOG_PRESET_SETTINGS",
  fetchContainerSettings: "FETCH_PRODUCT_CATALOG_CONTAINER_SETTINGS",
  createCategoryGroup: "CREATE_PRODUCT_CATALOG_CATEGORY_GROUP",
  deleteCategoryGroup: "DELETE_PRODUCT_CATALOG_CATEGORY_GROUP",
  createContainerGroup: "CREATE_PRODUCT_CATALOG_CONTAINER_GROUP",
  deleteContainerGroup: "DELETE_PRODUCT_CATALOG_CONTAINER_GROUP",
  createCategoryVariant: "CREATE_PRODUCT_CATALOG_CATEGORY_VARIANT",
  deleteCategoryVariant: "DELETE_PRODUCT_CATALOG_CATEGORY_VARIANT",
  createPresetSetting: "CREATE_PRODUCT_CATALOG_PRESET",
  deletePresetSetting: "DELETE_PRODUCT_CATALOG_PRESET",
  fetchTable: "FETCH_PRODUCT_CATALOG_TABLE",
  fetchChannelProducts: "FETCH_PRODUCT_CATALOG_CHANNEL_PRODUCTS",
  fetchCatalogSkus: "FETCH_PRODUCT_CATALOG_CATALOG_SKUS",
  updateProductSettings: "UPDATE_PRODUCT_CATALOG_PRODUCT_SETTINGS",
  createProduct: "CREATE_PRODUCT_CATALOG_PRODUCT",
  restoreDeletedProduct: "RESTORE_DELETED_CATALOG_PRODUCT",
  syncMetafieldsToShopify: "SYNC_METAFIELDS_TO_SHOPIFY",
};

const emptyColumnsMetadata = () => ({ by_view: {}, custom_views: [] });
const emptyDefaultsSettings = () => ({
  default_columns: DEFAULT_COLUMNS,
  default_pagination_pages: 50,
});

const initialState = {
  loading: false,
  loadingKeys: [],
  isFirstLoadComplete: false,
  error: null,
  /** Saved column snapshots per catalog view (`viewTable` id → column defs) */
  productCatalogColumnsMetadata: emptyColumnsMetadata(),
  columnsMetadataFetchStatus: "idle",
  productCatalogDefaultsSettings: emptyDefaultsSettings(),
  tierDiscountDrivers: { by_demand_tier_id: {} },
  tierDiscountDriversFetchStatus: "idle",
  defaultsSettingsFetchStatus: "idle",
  categorySettingsItems: [],
  containerSettingsItems: [],
  presetSettingsByType: {
    entry_engine: [],
    brand_pillar: [],
    demand_tier: [],
    intent: [],
  },
  channelProductsItems: [],
  categorySettingsFetchStatus: "idle",
  containerSettingsFetchStatus: "idle",
  presetSettingsFetchStatus: "idle",
  channelProductsFetchStatus: "idle",
  tableItems: [],
  tableTotal: 0,
  tableFetchStatus: "idle",
  latestTableRequestId: null,
  tablePagination: {
    currentPage: 1,
    itemsPerPage: 50,
    totalPages: 1,
    isTheLastPage: false,
    rangeRows: "",
  },
};

export const fetchProductCatalogTable = createAsyncThunk(
  "productCatalog/fetchProductCatalogTable",
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(PRODUCT_CATALOG_TABLE_URL, {
        params: {
          page: params.page,
          itemsPerPage: params.itemsPerPage,
          search: params.search,
          statuses: params.statuses,
          categories: params.categories,
          subcategories: params.subcategories,
          brandPillars: params.brandPillars,
          tiers: params.tiers,
          entryEngines: params.entryEngines,
          intents: params.intents,
          containerGroups: params.containerGroups,
          products: params.products,
          ...(params.catalogView ? { catalogView: params.catalogView } : {}),
          ...(params.sortBy ? { sortBy: params.sortBy } : {}),
          ...(params.sortOrder ? { sortOrder: params.sortOrder } : {}),
        },
      });
      return data;
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.errors ||
        error.message ||
        "Failed to load product catalog table";
      return rejectWithValue(message);
    }
  }
);

export const fetchProductCatalogCategorySettings = createAsyncThunk(
  "productCatalog/fetchProductCatalogCategorySettings",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(PRODUCT_CATALOG_CATEGORY_SETTINGS_URL);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.errors ||
        error.message ||
        "Failed to load product catalog category settings";
      return rejectWithValue(message);
    }
  }
);

export const fetchProductCatalogPresetSettings = createAsyncThunk(
  "productCatalog/fetchProductCatalogPresetSettings",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(PRODUCT_CATALOG_PRESET_SETTINGS_URL);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.errors ||
        error.message ||
        "Failed to load product catalog preset settings";
      return rejectWithValue(message);
    }
  }
);

export const fetchProductCatalogContainerSettings = createAsyncThunk(
  "productCatalog/fetchProductCatalogContainerSettings",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(PRODUCT_CATALOG_CONTAINER_SETTINGS_URL);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.errors ||
        error.message ||
        "Failed to load product catalog container groups";
      return rejectWithValue(message);
    }
  }
);

export const fetchProductCatalogChannelProducts = createAsyncThunk(
  "productCatalog/fetchProductCatalogChannelProducts",
  async ({ barcode, productId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(PRODUCT_CATALOG_CHANNEL_PRODUCTS_URL, {
        params: {
          barcode,
          productId,
        },
      });
      return data;
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.errors ||
        error.message ||
        "Failed to load channel products";
      return rejectWithValue(message);
    }
  }
);

export const fetchProductCatalogCatalogSkus = createAsyncThunk(
  "productCatalog/fetchProductCatalogCatalogSkus",
  async ({ search, excludeProductId, limit, includeNameMatch, includeBarcodeMatch, excludeByProductAndVariantsTab }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(PRODUCT_CATALOG_CATALOG_SKUS_URL, {
        params: {
          search: search || "",
          excludeProductId: excludeProductId || "",
          ...(limit != null ? { limit } : {}),
          ...(includeNameMatch ? { includeNameMatch: true } : {}),
          ...(includeBarcodeMatch ? { includeBarcodeMatch: true } : {}),
          ...(excludeByProductAndVariantsTab ? { excludeByProductAndVariantsTab: true } : {}),
        },
      });
      return data;
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.errors ||
        error.message ||
        "Failed to search catalog SKUs";
      return rejectWithValue(message);
    }
  }
);

export const fetchProductCatalogColumnsMetadata = createAsyncThunk(
  "productCatalog/fetchProductCatalogColumnsMetadata",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(METADATA_PRODUCT_CATALOG_COLUMNS);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.errors ||
        error.message ||
        "Failed to load product catalog columns";
      return rejectWithValue(message);
    }
  }
);

export const fetchProductCatalogDefaultsSettings = createAsyncThunk(
  "productCatalog/fetchProductCatalogDefaultsSettings",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(METADATA_PRODUCT_CATALOG_DEFAULTS_SETTINGS);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.errors ||
        error.message ||
        "Failed to load product catalog defaults settings";
      return rejectWithValue(message);
    }
  }
);

export const fetchTierDiscountDrivers = createAsyncThunk(
  "productCatalog/fetchTierDiscountDrivers",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(METADATA_TIER_DISCOUNT_DRIVERS);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || error.message || "Failed to load tier discount drivers"
      );
    }
  }
);

export const saveProductCatalogColumnsMetadata = createAsyncThunk(
  "productCatalog/saveProductCatalogColumnsMetadata",
  async (productCatalogColumns, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        METADATA_PRODUCT_CATALOG_COLUMNS,
        buildProductCatalogColumnsBody(productCatalogColumns)
      );
      return data;
    } catch (error) {
      let errMsg = "Failed to save product catalog columns";
      if (error.response?.data) {
        if (typeof error.response.data === "string") {
          errMsg = error.response.data;
        } else {
          errMsg =
            error.response.data.errors ||
            error.response.data.error ||
            errMsg;
        }
      }
      return rejectWithValue({ error: errMsg, productCatalogColumns });
    }
  }
);

export const syncMetafieldsToShopify = createAsyncThunk(
  "productCatalog/syncMetafieldsToShopify",
  async ({ productsToShopifySync, fieldToUpdate }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(PRODUCT_CATALOG_SYNC_METAFIELDS_TO_SHOPIFY_URL, {
        productsToShopifySync,
        fieldToUpdate,
      });
      return data;
    } catch (error) {
      const message =
      error?.response?.data ||
        "Failed to sync metafields to Shopify";
      return rejectWithValue(message);
    }
  }
);

export const saveProductCatalogDefaultsSettings = createAsyncThunk(
  "productCatalog/saveProductCatalogDefaultsSettings",
  async (settings, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        METADATA_PRODUCT_CATALOG_DEFAULTS_SETTINGS,
        buildProductCatalogDefaultsSettingsBody(settings)
      );
      return data;
    } catch (error) {
      let errMsg = "Failed to save product catalog defaults settings";
      if (error.response?.data) {
        if (typeof error.response.data === "string") {
          errMsg = error.response.data;
        } else {
          errMsg =
            error.response.data.errors ||
            error.response.data.error ||
            errMsg;
        }
      }
      return rejectWithValue({ error: errMsg, settings });
    }
  }
);

export const saveTierDiscountDrivers = createAsyncThunk(
  "productCatalog/saveTierDiscountDrivers",
  async (drivers, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(METADATA_TIER_DISCOUNT_DRIVERS, {
        [TIER_DISCOUNT_DRIVERS_FIELD]: normalizeTierDiscountDriversPayload(drivers),
      });
      return data;
    } catch (error) {
      return rejectWithValue({
        error: error.response?.data?.error || error.message || "Failed to save tier discount drivers",
        drivers,
      });
    }
  }
);

export const createProductCatalogCategoryGroup = createAsyncThunk(
  "productCatalog/createProductCatalogCategoryGroup",
  async ({ name }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(
        "/api/v1/product-catalog/settings/category-groups",
        { name }
      );
      await dispatch(fetchProductCatalogCategorySettings());
      return data;
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.errors ||
        error.message ||
        "Failed to create category group";
      return rejectWithValue(message);
    }
  }
);

export const deleteProductCatalogCategoryGroup = createAsyncThunk(
  "productCatalog/deleteProductCatalogCategoryGroup",
  async ({ id }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.delete(
        `/api/v1/product-catalog/settings/category-groups/${id}`
      );
      await dispatch(fetchProductCatalogCategorySettings());
      return data;
    } catch (error) {
      const data = error.response?.data;
      if (data && typeof data === "object") {
        return rejectWithValue(data);
      }
      const message =
        error.response?.data?.error ||
        error.response?.data?.errors ||
        error.message ||
        "Failed to delete category group";
      return rejectWithValue({ error: message });
    }
  }
);

export const createProductCatalogCategoryVariant = createAsyncThunk(
  "productCatalog/createProductCatalogCategoryVariant",
  async ({ groupId, name }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(
        "/api/v1/product-catalog/settings/category-variants",
        { groupId, name }
      );
      await dispatch(fetchProductCatalogCategorySettings());
      return data;
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.errors ||
        error.message ||
        "Failed to create subcategory";
      return rejectWithValue(message);
    }
  }
);

export const deleteProductCatalogCategoryVariant = createAsyncThunk(
  "productCatalog/deleteProductCatalogCategoryVariant",
  async ({ id }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.delete(
        `/api/v1/product-catalog/settings/category-variants/${id}`
      );
      await dispatch(fetchProductCatalogCategorySettings());
      return data;
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.errors ||
        error.message ||
        "Failed to delete subcategory";
      return rejectWithValue(message);
    }
  }
);

export const createProductCatalogPresetSetting = createAsyncThunk(
  "productCatalog/createProductCatalogPresetSetting",
  async ({ type, name }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(PRODUCT_CATALOG_PRESET_SETTINGS_URL, {
        type,
        name,
      });
      await dispatch(fetchProductCatalogPresetSettings());
      return data;
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.errors ||
        error.message ||
        "Failed to create preset";
      return rejectWithValue(message);
    }
  }
);

export const deleteProductCatalogPresetSetting = createAsyncThunk(
  "productCatalog/deleteProductCatalogPresetSetting",
  async ({ id }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.delete(
        `${PRODUCT_CATALOG_PRESET_SETTINGS_URL}/${id}`
      );
      await dispatch(fetchProductCatalogPresetSettings());
      return data;
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.errors ||
        error.message ||
        "Failed to delete preset";
      return rejectWithValue(message);
    }
  }
);

export const createProductCatalogContainerGroup = createAsyncThunk(
  "productCatalog/createProductCatalogContainerGroup",
  async ({ name }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(PRODUCT_CATALOG_CONTAINER_SETTINGS_URL, { name });
      await dispatch(fetchProductCatalogContainerSettings());
      return data;
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.errors ||
        error.message ||
        "Failed to create container group";
      return rejectWithValue(message);
    }
  }
);

export const deleteProductCatalogContainerGroup = createAsyncThunk(
  "productCatalog/deleteProductCatalogContainerGroup",
  async ({ id }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.delete(`${PRODUCT_CATALOG_CONTAINER_SETTINGS_URL}/${id}`);
      await dispatch(fetchProductCatalogContainerSettings());
      return data;
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.errors ||
        error.message ||
        "Failed to delete container group";
      return rejectWithValue(message);
    }
  }
);

export const createProductCatalogProduct = createAsyncThunk(
  "productCatalog/createProductCatalogProduct",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(PRODUCT_CATALOG_CREATE_PRODUCT_URL, payload);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.errors ||
        error.message ||
        "Failed to create product";
      return rejectWithValue(message);
    }
  }
);

export const restoreDeletedCatalogProduct = createAsyncThunk(
  "productCatalog/restoreDeletedCatalogProduct",
  async ({ productId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(PRODUCT_CATALOG_RESTORE_PRODUCT_URL, { productId });
      return data;
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.errors ||
        error.message ||
        "Failed to restore product";
      return rejectWithValue(message);
    }
  }
);

export const updateProductCatalogProductSettings = createAsyncThunk(
  "productCatalog/updateProductCatalogProductSettings",
  async ({
    productId,
    brand_pillar_id,
    demand_tier_id,
    intent_id,
    entry_engine_id,
    status,
    unit_weight_lbs,
    unit_length,
    unit_width,
    unit_height,
    category_group_id,
    subcategory_variant_id,
    is_packaging,
    is_supply,
    is_virtual,
    fbm_channel_product_id,
    fba_channel_product_id,
    packaging_product_id,
    forecastable,
    needs_review,
    lock_flag,
    replacement_exit,
    replacement_product_id,
    condition_of_product_id,
    condition_label,
    lead_time_days,
    transit_lead_time_days,
    moq,
    container_group_id,
    container_qty,
    carton_qty,
    launch_date,
    sunset_date,
    inactive_date,
    color,
    material,
    manual_msrp,
    shopify_planned_gross_price,
    amazon_planned_gross_price,
    planned_gross_price_note,
  }, { rejectWithValue }) => {
    try {
      const requestPayload = {
        productId,
        ...(brand_pillar_id !== undefined ? { brand_pillar_id } : {}),
        ...(demand_tier_id !== undefined ? { demand_tier_id } : {}),
        ...(intent_id !== undefined ? { intent_id } : {}),
        ...(entry_engine_id !== undefined ? { entry_engine_id } : {}),
        ...(status !== undefined ? { status } : {}),
        ...(unit_weight_lbs !== undefined ? { weight: unit_weight_lbs } : {}),
        ...(unit_length !== undefined ? { length: unit_length } : {}),
        ...(unit_width !== undefined ? { width: unit_width } : {}),
        ...(unit_height !== undefined ? { height: unit_height } : {}),
        ...(category_group_id !== undefined ? { category_group_id } : {}),
        ...(subcategory_variant_id !== undefined ? { subcategory_variant_id } : {}),
        ...(is_packaging !== undefined ? { is_packaging } : {}),
        ...(is_supply !== undefined ? { is_supply } : {}),
        ...(is_virtual !== undefined ? { is_virtual } : {}),
        ...(fbm_channel_product_id !== undefined ? { fbm_channel_product_id } : {}),
        ...(fba_channel_product_id !== undefined ? { fba_channel_product_id } : {}),
        ...(packaging_product_id !== undefined ? { packaging_product_id } : {}),
        ...(forecastable !== undefined ? { forecastable } : {}),
        ...(needs_review !== undefined ? { needs_review } : {}),
        ...(lock_flag !== undefined ? { lock_flag } : {}),
        ...(replacement_exit !== undefined ? { replacement_exit } : {}),
        ...(replacement_product_id !== undefined ? { replacement_product_id } : {}),
        ...(condition_of_product_id !== undefined ? { condition_of_product_id } : {}),
        ...(condition_label !== undefined ? { condition_label } : {}),
        ...(lead_time_days !== undefined ? { lead_time_days } : {}),
        ...(transit_lead_time_days !== undefined ? { transit_lead_time_days } : {}),
        ...(moq !== undefined ? { moq } : {}),
        ...(container_group_id !== undefined ? { container_group_id } : {}),
        ...(container_qty !== undefined ? { container_qty } : {}),
        ...(carton_qty !== undefined ? { carton_qty } : {}),
        ...(launch_date !== undefined ? { launch_date } : {}),
        ...(sunset_date !== undefined ? { sunset_date } : {}),
        ...(inactive_date !== undefined ? { inactive_date } : {}),
        ...(color !== undefined ? { color } : {}),
        ...(material !== undefined ? { material } : {}),
        ...(manual_msrp !== undefined ? { manual_msrp } : {}),
        ...(shopify_planned_gross_price !== undefined ? { shopify_planned_gross_price } : {}),
        ...(amazon_planned_gross_price !== undefined ? { amazon_planned_gross_price } : {}),
        ...(planned_gross_price_note !== undefined ? { planned_gross_price_note } : {}),
      };
      const { data } = await axios.post(PRODUCT_CATALOG_PRODUCT_SETTINGS_URL, requestPayload);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.errors ||
        error.message ||
        "Failed to update product settings";
      return rejectWithValue(message);
    }
  }
);

export const productCatalogSlice = createSlice({
  name: "productCatalog",
  initialState,
  reducers: {
    resetProductCatalogSlice: () => ({
      ...initialState,
    }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProductCatalogChannelProducts.pending, (state) => {
        state.error = null;
        state.channelProductsFetchStatus = "loading";
        state.loading = true;
        state.loadingKeys = [
          ...state.loadingKeys,
          ProductCatalogLoadingKeys.fetchChannelProducts,
        ];
      })
      .addCase(fetchProductCatalogChannelProducts.fulfilled, (state, action) => {
        state.channelProductsItems = action.payload?.items || [];
        state.channelProductsFetchStatus = "succeeded";
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.fetchChannelProducts,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
      })
      .addCase(fetchProductCatalogChannelProducts.rejected, (state, action) => {
        state.error = action.payload || "Failed to load channel products";
        state.channelProductsItems = [];
        state.channelProductsFetchStatus = "failed";
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.fetchChannelProducts,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
      })
      .addCase(fetchProductCatalogColumnsMetadata.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.columnsMetadataFetchStatus = "loading";
        state.loadingKeys = [
          ...state.loadingKeys,
          ProductCatalogLoadingKeys.fetchColumnsMetadata,
        ];
      })
      .addCase(fetchProductCatalogColumnsMetadata.fulfilled, (state, action) => {
        state.productCatalogColumnsMetadata =
          normalizeProductCatalogColumnsPayload(action.payload);
        state.columnsMetadataFetchStatus = "succeeded";
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.fetchColumnsMetadata,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
      })
      .addCase(fetchProductCatalogColumnsMetadata.rejected, (state, action) => {
        state.error = action.payload || "Failed to load product catalog columns";
        state.productCatalogColumnsMetadata = emptyColumnsMetadata();
        state.columnsMetadataFetchStatus = "failed";
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.fetchColumnsMetadata,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
      })
      .addCase(fetchProductCatalogDefaultsSettings.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.defaultsSettingsFetchStatus = "loading";
        state.loadingKeys = [
          ...state.loadingKeys,
          ProductCatalogLoadingKeys.fetchDefaultsSettings,
        ];
      })
      .addCase(fetchProductCatalogDefaultsSettings.fulfilled, (state, action) => {
        state.productCatalogDefaultsSettings =
          normalizeProductCatalogDefaultsSettingsPayload(action.payload);
        state.defaultsSettingsFetchStatus = "succeeded";
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.fetchDefaultsSettings,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
      })
      .addCase(fetchProductCatalogDefaultsSettings.rejected, (state, action) => {
        state.error =
          action.payload || "Failed to load product catalog defaults settings";
        state.productCatalogDefaultsSettings = emptyDefaultsSettings();
        state.defaultsSettingsFetchStatus = "failed";
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.fetchDefaultsSettings,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
      })
      .addCase(fetchTierDiscountDrivers.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.tierDiscountDriversFetchStatus = "loading";
        state.loadingKeys = [...state.loadingKeys, ProductCatalogLoadingKeys.fetchTierDiscountDrivers];
      })
      .addCase(fetchTierDiscountDrivers.fulfilled, (state, action) => {
        state.tierDiscountDrivers = normalizeTierDiscountDriversPayload(action.payload);
        state.tierDiscountDriversFetchStatus = "succeeded";
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.fetchTierDiscountDrivers,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
      })
      .addCase(fetchTierDiscountDrivers.rejected, (state, action) => {
        state.error = action.payload || "Failed to load tier discount drivers";
        state.tierDiscountDrivers = { by_demand_tier_id: {} };
        state.tierDiscountDriversFetchStatus = "failed";
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.fetchTierDiscountDrivers,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
      })
      .addCase(fetchProductCatalogCategorySettings.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.categorySettingsFetchStatus = "loading";
        state.loadingKeys = [
          ...state.loadingKeys,
          ProductCatalogLoadingKeys.fetchCategorySettings,
        ];
      })
      .addCase(fetchProductCatalogCategorySettings.fulfilled, (state, action) => {
        state.categorySettingsItems = action.payload?.items || [];
        state.categorySettingsFetchStatus = "succeeded";
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.fetchCategorySettings,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
      })
      .addCase(fetchProductCatalogCategorySettings.rejected, (state, action) => {
        state.error =
          action.payload || "Failed to load product catalog category settings";
        state.categorySettingsItems = [];
        state.categorySettingsFetchStatus = "failed";
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.fetchCategorySettings,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
      })
      .addCase(fetchProductCatalogPresetSettings.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.presetSettingsFetchStatus = "loading";
        state.loadingKeys = [
          ...state.loadingKeys,
          ProductCatalogLoadingKeys.fetchPresetSettings,
        ];
      })
      .addCase(fetchProductCatalogPresetSettings.fulfilled, (state, action) => {
        state.presetSettingsByType = action.payload?.byType || {
          entry_engine: [],
          brand_pillar: [],
          demand_tier: [],
          intent: [],
        };
        state.presetSettingsFetchStatus = "succeeded";
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.fetchPresetSettings,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
      })
      .addCase(fetchProductCatalogPresetSettings.rejected, (state, action) => {
        state.error =
          action.payload || "Failed to load product catalog preset settings";
        state.presetSettingsByType = {
          entry_engine: [],
          brand_pillar: [],
          demand_tier: [],
          intent: [],
        };
        state.presetSettingsFetchStatus = "failed";
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.fetchPresetSettings,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
      })
      .addCase(fetchProductCatalogContainerSettings.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.containerSettingsFetchStatus = "loading";
        state.loadingKeys = [
          ...state.loadingKeys,
          ProductCatalogLoadingKeys.fetchContainerSettings,
        ];
      })
      .addCase(fetchProductCatalogContainerSettings.fulfilled, (state, action) => {
        state.containerSettingsItems = action.payload?.items || [];
        state.containerSettingsFetchStatus = "succeeded";
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.fetchContainerSettings,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
      })
      .addCase(fetchProductCatalogContainerSettings.rejected, (state, action) => {
        state.error =
          action.payload || "Failed to load product catalog container groups";
        state.containerSettingsItems = [];
        state.containerSettingsFetchStatus = "failed";
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.fetchContainerSettings,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
      })
      .addCase(fetchProductCatalogTable.pending, (state, action) => {
        state.error = null;
        state.latestTableRequestId = action.meta.requestId;
        state.tableFetchStatus = "loading";
        state.loading = true;
        if (!state.loadingKeys.includes(ProductCatalogLoadingKeys.fetchTable)) {
          state.loadingKeys = [
            ...state.loadingKeys,
            ProductCatalogLoadingKeys.fetchTable,
          ];
        }
      })
      .addCase(fetchProductCatalogTable.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestTableRequestId) return;
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.fetchTable,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        state.tableItems = action.payload?.items || [];
        state.tableTotal = action.payload?.total || 0;
        state.tablePagination = action.payload?.pagination || state.tablePagination;
        state.tableFetchStatus = "succeeded";
        state.isFirstLoadComplete = true;
      })
      .addCase(fetchProductCatalogTable.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestTableRequestId) return;
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.fetchTable,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        state.error = action.payload || "Failed to load product catalog table";
        state.tableItems = [];
        state.tableTotal = 0;
        state.tablePagination = initialState.tablePagination;
        state.tableFetchStatus = "failed";
        state.isFirstLoadComplete = true;
      })
      .addCase(saveProductCatalogColumnsMetadata.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.loadingKeys = [
          ...state.loadingKeys,
          ProductCatalogLoadingKeys.saveColumnsMetadata,
        ];
      })
      .addCase(saveProductCatalogColumnsMetadata.fulfilled, (state, action) => {
        const normalized = normalizeProductCatalogColumnsPayload(action.payload);
        if (Object.keys(normalized.by_view).length > 0) {
          state.productCatalogColumnsMetadata = normalized;
        } else if (action.meta?.arg?.by_view) {
          state.productCatalogColumnsMetadata = {
            by_view: action.meta.arg.by_view,
            custom_views: normalizeProductCatalogCustomViews(action.meta.arg.custom_views),
          };
        }
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.saveColumnsMetadata,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        toast.success("Columns updated successfully.", {
          position: "bottom-right",
        });
      })
      .addCase(saveProductCatalogColumnsMetadata.rejected, (state, action) => {
        state.error =
          action.payload?.error || action.error?.message || "Save failed";
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.saveColumnsMetadata,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        toast.error(
          `Columns can't be updated. ${
            action.payload?.error || action.error?.message || ""
          }`,
          { position: "bottom-right" }
        );
      })
      .addCase(saveProductCatalogDefaultsSettings.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.loadingKeys = [
          ...state.loadingKeys,
          ProductCatalogLoadingKeys.saveDefaultsSettings,
        ];
      })
      .addCase(saveProductCatalogDefaultsSettings.fulfilled, (state, action) => {
        state.productCatalogDefaultsSettings =
          normalizeProductCatalogDefaultsSettingsPayload(action.payload);
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.saveDefaultsSettings,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        toast.success("Default settings updated successfully.", {
          position: "bottom-right",
        });
      })
      .addCase(saveProductCatalogDefaultsSettings.rejected, (state, action) => {
        state.error =
          action.payload?.error || action.error?.message || "Save failed";
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.saveDefaultsSettings,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        toast.error(
          `Default settings can't be updated. ${
            action.payload?.error || action.error?.message || ""
          }`,
          { position: "bottom-right" }
        );
      })
      .addCase(saveTierDiscountDrivers.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, ProductCatalogLoadingKeys.saveTierDiscountDrivers];
      })
      .addCase(saveTierDiscountDrivers.fulfilled, (state, action) => {
        state.tierDiscountDrivers = normalizeTierDiscountDriversPayload(action.payload);
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.saveTierDiscountDrivers,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        toast.success("Tier discount drivers updated successfully.", { position: "bottom-right" });
      })
      .addCase(saveTierDiscountDrivers.rejected, (state, action) => {
        state.error = action.payload?.error || action.error?.message || "Save failed";
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.saveTierDiscountDrivers,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        toast.error(
          `Tier discount drivers can't be updated. ${action.payload?.error || action.error?.message || ""}`,
          { position: "bottom-right" }
        );
      })
      .addCase(createProductCatalogCategoryGroup.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.loadingKeys = [
          ...state.loadingKeys,
          ProductCatalogLoadingKeys.createCategoryGroup,
        ];
      })
      .addCase(createProductCatalogCategoryGroup.fulfilled, (state) => {
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.createCategoryGroup,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        toast.success("Category group created successfully.", {
          position: "bottom-right",
        });
      })
      .addCase(createProductCatalogCategoryGroup.rejected, (state, action) => {
        state.error = action.payload || action.error?.message || "Save failed";
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.createCategoryGroup,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        toast.error(
          `Category group can't be created. ${action.payload || action.error?.message || ""}`,
          { position: "bottom-right" }
        );
      })
      .addCase(deleteProductCatalogCategoryGroup.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.loadingKeys = [
          ...state.loadingKeys,
          ProductCatalogLoadingKeys.deleteCategoryGroup,
        ];
      })
      .addCase(deleteProductCatalogCategoryGroup.fulfilled, (state) => {
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.deleteCategoryGroup,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        toast.success("Category group deleted successfully.", {
          position: "bottom-right",
        });
      })
      .addCase(deleteProductCatalogCategoryGroup.rejected, (state, action) => {
        const p = action.payload;
        const errText =
          typeof p === "string"
            ? p
            : p?.error ||
              p?.errors ||
              action.error?.message ||
              "Delete failed";
        state.error = errText;
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.deleteCategoryGroup,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        const inUse =
          typeof p === "object" && p != null ? p.inUseCount : undefined;
        const groupsInUse =
          typeof p === "object" && p != null ? p.productGroupsInUse : undefined;
        const countsSuffix =
          inUse != null || groupsInUse != null
            ? ` In use by ${inUse ?? 0} products and ${groupsInUse ?? 0} product groups.`
            : "";
        toast.error(`Category group can't be deleted. ${errText}${countsSuffix}`, {
          position: "bottom-right",
        });
      })
      .addCase(syncMetafieldsToShopify.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.loadingKeys = [
          ...state.loadingKeys,
          ProductCatalogLoadingKeys.syncMetafieldsToShopify,
        ];
      })
      .addCase(syncMetafieldsToShopify.fulfilled, (state) => {
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.syncMetafieldsToShopify,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        toast.success("Metafields synced to Shopify successfully.", {
          position: "bottom-right",
        });
      })
      .addCase(syncMetafieldsToShopify.rejected, (state, action) => {
        console.log(action)
        state.error = action.payload || action.error?.message || "Save failed";
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.syncMetafieldsToShopify,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        toast.error(
          `Metafields can't be synced to Shopify. ${action.payload || action.error?.message || ""}`,
          { position: "bottom-right" }
        );
      })
      .addCase(createProductCatalogCategoryVariant.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.loadingKeys = [
          ...state.loadingKeys,
          ProductCatalogLoadingKeys.createCategoryVariant,
        ];
      })
      .addCase(createProductCatalogCategoryVariant.fulfilled, (state) => {
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.createCategoryVariant,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        toast.success("Subcategory created successfully.", {
          position: "bottom-right",
        });
      })
      .addCase(createProductCatalogCategoryVariant.rejected, (state, action) => {
        state.error = action.payload || action.error?.message || "Save failed";
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.createCategoryVariant,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        toast.error(
          `Subcategory can't be created. ${action.payload || action.error?.message || ""}`,
          { position: "bottom-right" }
        );
      })
      .addCase(deleteProductCatalogCategoryVariant.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.loadingKeys = [
          ...state.loadingKeys,
          ProductCatalogLoadingKeys.deleteCategoryVariant,
        ];
      })
      .addCase(deleteProductCatalogCategoryVariant.fulfilled, (state) => {
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.deleteCategoryVariant,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        toast.success("Subcategory deleted successfully.", {
          position: "bottom-right",
        });
      })
      .addCase(deleteProductCatalogCategoryVariant.rejected, (state, action) => {
        state.error = action.payload || action.error?.message || "Delete failed";
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.deleteCategoryVariant,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        toast.error(
          `Subcategory can't be deleted. ${action.payload || action.error?.message || ""}`,
          { position: "bottom-right" }
        );
      })
      .addCase(createProductCatalogPresetSetting.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.loadingKeys = [
          ...state.loadingKeys,
          ProductCatalogLoadingKeys.createPresetSetting,
        ];
      })
      .addCase(createProductCatalogPresetSetting.fulfilled, (state) => {
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.createPresetSetting,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        toast.success("Preset created successfully.", {
          position: "bottom-right",
        });
      })
      .addCase(createProductCatalogPresetSetting.rejected, (state, action) => {
        state.error = action.payload || action.error?.message || "Save failed";
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.createPresetSetting,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        toast.error(
          `Preset can't be created. ${action.payload || action.error?.message || ""}`,
          { position: "bottom-right" }
        );
      })
      .addCase(deleteProductCatalogPresetSetting.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.loadingKeys = [
          ...state.loadingKeys,
          ProductCatalogLoadingKeys.deletePresetSetting,
        ];
      })
      .addCase(deleteProductCatalogPresetSetting.fulfilled, (state) => {
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.deletePresetSetting,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        toast.success("Preset deleted successfully.", {
          position: "bottom-right",
        });
      })
      .addCase(deleteProductCatalogPresetSetting.rejected, (state, action) => {
        state.error = action.payload || action.error?.message || "Delete failed";
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.deletePresetSetting,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        toast.error(
          `Preset can't be deleted. ${action.payload || action.error?.message || ""}`,
          { position: "bottom-right" }
        );
      })
      .addCase(createProductCatalogContainerGroup.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.loadingKeys = [
          ...state.loadingKeys,
          ProductCatalogLoadingKeys.createContainerGroup,
        ];
      })
      .addCase(createProductCatalogContainerGroup.fulfilled, (state) => {
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.createContainerGroup,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        toast.success("Container group created successfully.", {
          position: "bottom-right",
        });
      })
      .addCase(createProductCatalogContainerGroup.rejected, (state, action) => {
        state.error = action.payload || action.error?.message || "Save failed";
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.createContainerGroup,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        toast.error(
          `Container group can't be created. ${action.payload || action.error?.message || ""}`,
          { position: "bottom-right" }
        );
      })
      .addCase(deleteProductCatalogContainerGroup.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.loadingKeys = [
          ...state.loadingKeys,
          ProductCatalogLoadingKeys.deleteContainerGroup,
        ];
      })
      .addCase(deleteProductCatalogContainerGroup.fulfilled, (state) => {
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.deleteContainerGroup,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        toast.success("Container group deleted successfully.", {
          position: "bottom-right",
        });
      })
      .addCase(deleteProductCatalogContainerGroup.rejected, (state, action) => {
        state.error = action.payload || action.error?.message || "Delete failed";
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.deleteContainerGroup,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        toast.error(
          `Container group can't be deleted. ${action.payload || action.error?.message || ""}`,
          { position: "bottom-right" }
        );
      })
      .addCase(updateProductCatalogProductSettings.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.loadingKeys = [
          ...state.loadingKeys,
          ProductCatalogLoadingKeys.updateProductSettings,
        ];
      })
      .addCase(updateProductCatalogProductSettings.fulfilled, (state, action) => {
        const updated = action.payload?.item;
        const changedKeys = Object.keys(action.meta?.arg ?? {});
        if (updated?.id) {
          state.tableItems = (state.tableItems || []).map((row) =>
            row.id === updated.id
              ? mergeCatalogTableRowAfterSettingsUpdate(row, updated, changedKeys)
              : row
          );
        }
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.updateProductSettings,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        if (!action.meta.arg?.skipSuccessToast) {
          toast.success("Product settings updated successfully.", {
            position: "bottom-right",
          });
        }
      })
      .addCase(updateProductCatalogProductSettings.rejected, (state, action) => {
        state.error = action.payload || action.error?.message || "Update failed";
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.updateProductSettings,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        toast.error(
          `Product settings can't be updated. ${action.payload || action.error?.message || ""}`,
          { position: "bottom-right" }
        );
      })
      .addCase(createProductCatalogProduct.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, ProductCatalogLoadingKeys.createProduct];
      })
      .addCase(createProductCatalogProduct.fulfilled, (state) => {
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.createProduct,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
      })
      .addCase(createProductCatalogProduct.rejected, (state, action) => {
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.createProduct,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        state.error = action.payload || action.error?.message || "Create failed";
      })
      .addCase(restoreDeletedCatalogProduct.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.loadingKeys = [
          ...state.loadingKeys,
          ProductCatalogLoadingKeys.restoreDeletedProduct,
        ];
      })
      .addCase(restoreDeletedCatalogProduct.fulfilled, (state) => {
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.restoreDeletedProduct,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        toast.success("Product restored.", { position: "bottom-right" });
      })
      .addCase(restoreDeletedCatalogProduct.rejected, (state, action) => {
        state.loadingKeys = removeLoadingKey(
          ProductCatalogLoadingKeys.restoreDeletedProduct,
          state.loadingKeys
        );
        state.loading = state.loadingKeys.length > 0;
        state.error = action.payload || action.error?.message || "Restore failed";
        toast.error(
          `Product can't be restored. ${action.payload || action.error?.message || ""}`,
          { position: "bottom-right" }
        );
      });
  },
});

export const { resetProductCatalogSlice } = productCatalogSlice.actions;

export default productCatalogSlice.reducer;
